import { RestError } from '@azure/core-http';
import {
  DeliveryContainerUtilisationPeakBandsDto,
  DeliveryContainerUtilisationTurnoverDto,
  KnownHistoricalUtilisationPeakBandsMetadataGroupBy
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { Container } from '@mui/material';
import dayjs from 'dayjs';
import { Paths, ProjectPathParams } from '../../../app/router/app.router';
import { PermissionBoundary } from '../../../core/permissions/permission-boundary.component';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { DeliveryContainerUtilisationCard } from '../../../ui/components/pure/reports/delivery-container-utilisation/delivery-container-utilisation-card/delivery-container-utilisation-card.component';
import { getDateLabelUnit, DateRange } from '../_shared/date-range';
import { makeDeliveryContainerCapacityData } from './delivery-container-capacity-props.map';
import { makeDeliveryContainerUtilisationData } from './delivery-container-utilisation-props.map';
import { useDeliveryContainerUtilisationReport } from './delivery-container-utilisation-report.hook';

export function DeliveryContainerUtilisationReportPage({
  params
}: {
  params: ProjectPathParams<Paths.deliveryContainerUtilisationReport>;
}) {
  const { data, state, handlers, options } = useDeliveryContainerUtilisationReport(params.projectID);

  const fatalError = pickFatalError(data.utilisation.error, data.capacity.error);
  if (fatalError) {
    return <ErrorFullPage preset={fatalError} />;
  }

  const fromDate = state.utilisationQuery.fromDate ?? dayjs().subtract(1, 'week').toDate();
  const toDate = state.utilisationQuery.toDate;
  const dateLabelUnit = getDateLabelUnit(state.range, toDate, fromDate);
  const compactTimeFormat = state.range === DateRange.Day || state.range === DateRange.Week;
  // Drop heatmap day-headers + in-cell labels on ranges longer than a month.
  const compactHeatmap = state.range === DateRange.HalfYear || state.range === DateRange.Year;

  const utilisationPlaceholderData: DeliveryContainerUtilisationTurnoverDto = {
    metadata: {
      fromDate,
      toDate,
      groupBy: state.utilisationQuery.groupBy,
      hubID: state.utilisationQuery.hubID,
      projectID: params.projectID
    },
    groups: []
  };
  const capacityPlaceholderData: DeliveryContainerUtilisationPeakBandsDto = {
    metadata: {
      fromDate,
      toDate,
      groupBy: state.utilisationQuery.groupBy as unknown as KnownHistoricalUtilisationPeakBandsMetadataGroupBy,
      hubID: state.utilisationQuery.hubID,
      projectID: params.projectID
    },
    groups: []
  };

  const utilisationDto = data.utilisation.data || utilisationPlaceholderData;
  const utilisationModel = makeDeliveryContainerUtilisationData(utilisationDto);
  const capacityModel = makeDeliveryContainerCapacityData(data.capacity.data || capacityPlaceholderData);
  const utilisationIsLoading = data.utilisation.isLoading || data.utilisation.isFetching;

  return (
    <Container>
      <PermissionBoundary
        availableUiFeatures={['experimental']}
        notify={{
          render() {
            return <ErrorFullPage preset="ACCESS_DENIED" />;
          }
        }}
      >
        <WindowTitle pageTitle="Reports" detail="Delivery Container Utilisation" />
        <ListPageHeader
          title="Delivery Container Utilisation"
          subtitle="Utilisation heatmap and capacity bands over time"
        />
        <DeliveryContainerUtilisationCard
          HeaderProps={{
            dateRangeSelect: {
              onChange: handlers.onSelectDateRange,
              options: options.dateRangeOptions,
              value: state.range
            },
            groupingSelect: {
              onChange: handlers.onSelectGroupBy,
              options: options.groupByOptions,
              value: state.utilisationQuery.groupBy
            },
            hubSelect: {
              onChange: handlers.onSelectHub,
              options: options.hubOptions,
              value: state.utilisationQuery.hubID || 'all'
            }
          }}
          UtilisationProps={{
            model: utilisationModel,
            isLoading: utilisationIsLoading,
            compact: compactHeatmap
          }}
          CapacityProps={{
            data: capacityModel.data,
            groupNames: capacityModel.groupNames,
            fromDate,
            toDate,
            dateLabelUnit,
            compactTimeFormat,
            isLoading: data.capacity.isLoading || data.capacity.isFetching
          }}
          utilisationError={!!data.utilisation.error}
          capacityError={!!data.capacity.error}
        />
      </PermissionBoundary>
    </Container>
  );
}

// Only escalate to a full-page error preset when BOTH queries fail with the same auth/not-found status.
// Otherwise we let the per-chart error placeholder handle the partial failure.
function pickFatalError(
  utilisationErr: unknown,
  capacityErr: unknown
): 'GLOBAL_ACCESS_DENIED' | 'ACCESS_DENIED' | 'NOT_FOUND' | undefined {
  const a = (utilisationErr as RestError | undefined)?.statusCode;
  const b = (capacityErr as RestError | undefined)?.statusCode;
  if (a === 401 && b === 401) return 'GLOBAL_ACCESS_DENIED';
  if (a === 403 && b === 403) return 'ACCESS_DENIED';
  if (a === 404 && b === 404) return 'NOT_FOUND';
  return undefined;
}
